import React from 'react';
import { Alert, Box, Container, CssBaseline, Typography } from '@mui/material';
import AddCardPayment from '../components/AddCardPayment';
import headerLogo from '../../images/header-logo.png';
import { darkPageBg, darkFormContainerSx } from '../components/darkFormStyles';

interface RegistrationCardDetailsProps {
  /** Stripe customer ID created earlier in the registration flow. */
  customerId: string;
  /** Called once a payment method has been successfully attached. */
  onSuccess: () => void;
}

/**
 * Mandatory card-collection step shown after Auth0 user creation / profile
 * save and before subscription selection. Reuses AddCardPayment directly
 * (no Stripe Elements/setup logic is duplicated here) to attach a payment
 * method to the registrant's Stripe customer so recurring billing can occur
 * automatically on the subscription's due dates. No charge is made here —
 * only the card is attached.
 */
const RegistrationCardDetails: React.FC<RegistrationCardDetailsProps> = ({
  customerId,
  onSuccess,
}) => {
  return (
    <Box sx={{ bgcolor: darkPageBg, minHeight: '100vh', py: 8 }}>
      <Container
        component="main"
        maxWidth="sm"
        sx={{ ...darkFormContainerSx, mt: 4, mb: 4, p: 4 }}
      >
        <CssBaseline />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Box
            component="img"
            src={headerLogo}
            alt="Barbers Limited"
            sx={{ width: 88, height: 88, objectFit: 'contain', mb: 1 }}
          />
          <Typography
            component="h1"
            variant="h5"
            fontWeight="bold"
            sx={{ color: '#fff' }}
          >
            Add Your Payment Details
          </Typography>
          <Typography
            variant="body2"
            sx={{ mt: 1, textAlign: 'center', color: 'rgba(255,255,255,0.65)' }}
          >
            A valid card is required to continue. You will not be charged now —
            this secures automatic billing on your subscription's due dates.
          </Typography>

          {!customerId && (
            <Alert severity="error" sx={{ mt: 2, width: '100%' }}>
              Missing customer reference. Please restart registration.
            </Alert>
          )}

          {customerId && (
            <Box sx={{ mt: 1, width: '100%' }}>
              <AddCardPayment customerId={customerId} onSuccess={onSuccess} />
            </Box>
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default RegistrationCardDetails;
